/**
 * AddVisitForm — manual create-visit form. Required: rating + visited_at.
 *
 * Per AC-039 / UX-AC-039: forms-only flow at /p/:placeId/visits/new; reused
 * inline from a sheet on the Place detail page.
 */
import * as React from 'react';
import type { PlaceId, TagId } from '@shared/types';
import { Button } from './ui/Button';
import { TextArea } from './ui/TextArea';
import { TextInput } from './ui/TextInput';
import { FormField } from './ui/FormField';
import { DotRating, type DotValue } from './ui/DotRating';
import { useActiveHouseholdId } from '../lib/household';
import { useCreateVisit, useTags } from '../lib/queries';
import { useToast } from '../lib/store';
import { useErrorToasts } from '../lib/errorToast';
import { cn } from '../lib/cn';

export interface AddVisitFormProps {
  placeId: PlaceId;
  onSuccess?: () => void;
  onCancel?: () => void;
}

function isoLocalDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function AddVisitForm({
  placeId,
  onSuccess,
  onCancel,
}: AddVisitFormProps) {
  const householdId = useActiveHouseholdId();
  const tagsQ = useTags(householdId);
  const create = useCreateVisit(householdId);
  const toast = useToast();
  const errors = useErrorToasts();

  const [visitedAt, setVisitedAt] = React.useState<string>(isoLocalDate());
  const [rating, setRating] = React.useState<DotValue>(0);
  const [notes, setNotes] = React.useState('');
  const [tagIds, setTagIds] = React.useState<TagId[]>([]);
  const [touched, setTouched] = React.useState(false);

  const tags = tagsQ.data?.tags ?? [];

  const dateError =
    touched && !visitedAt ? 'When did you visit?' : '';
  const ratingError =
    touched && rating === 0 ? 'Pick a rating from 1 to 5 dots.' : '';
  const formInvalid = Boolean(dateError) || Boolean(ratingError);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched(true);
    if (!visitedAt || rating === 0) return;
    try {
      await create.mutateAsync({
        place_id: placeId,
        visited_at: `${visitedAt}T12:00:00Z`,
        rating: rating as 1 | 2 | 3 | 4 | 5,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(tagIds.length ? { tag_ids: tagIds } : {}),
      });
      toast.success('Entry saved.');
      onSuccess?.();
    } catch (err) {
      if (!errors.handle(err)) {
        toast.error(
          err instanceof Error ? err.message : "We couldn't save that entry.",
        );
      }
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-6">
      {formInvalid && touched ? (
        <div
          className="px-4 py-3 border border-error-muted/40 bg-error-soft"
          role="alert"
        >
          <p className="font-serif italic text-sm text-error-muted leading-snug">
            A few details need attention.
          </p>
        </div>
      ) : null}

      <FormField
        label="Date"
        htmlFor="add-visit-date"
        {...(dateError ? { error: dateError } : {})}
      >
        <TextInput
          id="add-visit-date"
          type="date"
          value={visitedAt}
          onChange={(e) => setVisitedAt(e.target.value)}
          required
        />
      </FormField>

      <FormField
        label="Rating"
        {...(ratingError ? { error: ratingError } : {})}
      >
        <DotRating
          value={rating}
          onChange={(v) => setRating(v)}
          size="lg"
        />
      </FormField>

      <FormField label="Notes" optional htmlFor="add-visit-notes">
        <TextArea
          id="add-visit-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What stood out about this visit?"
          minRows={4}
        />
      </FormField>

      {tags.length ? (
        <FormField label="Tags" optional>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => {
              const active = tagIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() =>
                    setTagIds((prev) =>
                      active ? prev.filter((id) => id !== t.id) : [...prev, t.id],
                    )
                  }
                  aria-pressed={active}
                  className={cn(
                    'px-3 py-1 border text-xs lowercase rounded-full',
                    'transition-colors duration-200 ease-out-slow',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
                    active
                      ? 'bg-accent-soft border-accent text-accent'
                      : 'bg-transparent border-ink-200 text-ink-700 hover:bg-surface-2',
                  )}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        </FormField>
      ) : null}

      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          loading={create.isPending}
          loadingLabel="Saving…"
          disabled={create.isPending}
        >
          Save entry
        </Button>
      </div>
    </form>
  );
}
